import { getUserVideoProgress, saveVideoProgress } from '@/api/api'

const HEARTBEAT_PLAYED_SECONDS = 15
const MIN_SAVE_POSITION_DELTA = 2
const PAUSE_SAVE_DELAY_MS = 500
const SEEK_SAVE_DELAY_MS = 800
const MAX_VALID_PROGRESS_DELTA = 2.5
const CONTINUE_PLAY_TIP_DURATION_MS = 5000
const CONTINUE_PLAY_READY_STATE = 3

export default {
  data() {
    return {
      hasCheckedProgress: false,
      isProgressLoaded: false,
      lastSavedTime: 0,
      actualPlayTime: 0,
      videoProgressId: null,
      videoProgressPlayer: null,
      progressSaveTimer: null,
      playTimerInterval: null,
      progressPauseTimer: null,
      progressSeekTimer: null,
      progressSaveInFlight: false,
      pendingProgressSave: false,
      lastPlaybackSampleTime: null,
      isProgressSeeking: false,
      progressEventListenersBound: false,
      pendingResumeTime: null,
      pendingResumeDuration: 0,
      hasAppliedPendingResume: false,
      continuePlayTipVisible: false,
      continuePlayTipText: '',
      continuePlayTipTime: '',
      continuePlayTipTimer: null,
      pendingContinuePlayTip: null
    }
  },

  methods: {
    async initVideoProgress(videoId, videoPlayerRef) {
      if (!videoId || !videoPlayerRef || this.isProgressLoaded) {
        return
      }

      this.isProgressLoaded = true
      this.videoProgressId = videoId
      this.videoProgressPlayer = videoPlayerRef
      this.lastPlaybackSampleTime = null
      this.actualPlayTime = 0
      this.pendingProgressSave = false

      await this.checkVideoProgress()
      this.addProgressEventListeners()
      this.addBeforeUnloadListener()
      this.tryApplyPendingResume()
    },

    async checkVideoProgress() {
      if (this.hasCheckedProgress) return

      try {
        const res = await getUserVideoProgress({ videoId: this.videoProgressId })
        if (res.code === 1000 && res.data) {
          const currentTime = Number(res.data.currentTime) || 0
          const duration = Number(res.data.duration) || 0
          const progressPercent = duration > 0
            ? Number((((Number(res.data.progress) || ((currentTime / duration) * 100)))).toFixed(1))
            : 0

          if (currentTime > 0 && progressPercent < 95) {
            this.preparePendingResume(currentTime, duration)
          }
        }
        if (typeof this.onPlaybackNetworkRecovered === 'function') {
          this.onPlaybackNetworkRecovered({ source: 'getUserVideoProgress' })
        }
      } catch (error) {
        if (typeof this.onPlaybackNetworkError === 'function') {
          this.onPlaybackNetworkError(error, { source: 'getUserVideoProgress' })
        }
      } finally {
        this.hasCheckedProgress = true
        this.isProgressLoaded = true
      }
    },

    preparePendingResume(currentTime, duration) {
      const safeTime = this.roundSeconds(Math.max(0, Number(currentTime) || 0))
      if (!safeTime) {
        return
      }

      this.pendingResumeTime = safeTime
      this.pendingResumeDuration = this.roundSeconds(Math.max(0, Number(duration) || 0))
      this.hasAppliedPendingResume = false
      this.tryApplyPendingResume()
    },

    tryApplyPendingResume() {
      if (this.pendingResumeTime === null || this.hasAppliedPendingResume || !this.videoProgressPlayer) {
        return false
      }

      const readyState = Number(this.videoProgressPlayer.readyState) || 0
      const duration = Number(this.videoProgressPlayer.duration)
      const hasDuration = Number.isFinite(duration) && duration > 0
      if (readyState < CONTINUE_PLAY_READY_STATE && !hasDuration) {
        return false
      }

      const resumeTime = this.pendingResumeTime
      const resumeDuration = this.pendingResumeDuration
      this.seekToSavedProgress(resumeTime)
      this.hasAppliedPendingResume = true
      this.pendingResumeTime = null
      this.pendingResumeDuration = 0
      this.pendingContinuePlayTip = {
        currentTime: resumeTime,
        duration: resumeDuration
      }
      if (!this.videoProgressPlayer.paused) {
        this.flushContinuePlayTip()
      }
      return true
    },

    flushContinuePlayTip() {
      if (!this.pendingContinuePlayTip) {
        return
      }

      const { currentTime, duration } = this.pendingContinuePlayTip
      this.pendingContinuePlayTip = null
      this.showContinuePlayTip(currentTime, duration)
    },

    showContinuePlayTip(currentTime, duration) {
      const formattedTime = this.formatTime(currentTime)
      const progressText = duration > 0
        ? ` · 已看 ${Math.min(100, Math.max(0, Math.round((currentTime / duration) * 100)))}%`
        : ''

      this.continuePlayTipTime = formattedTime
      this.continuePlayTipText = `已为您自动续播至 ${formattedTime}${progressText}`
      this.continuePlayTipVisible = true
      this.startContinuePlayTipTimer()
    },

    startContinuePlayTipTimer() {
      this.clearContinuePlayTipTimer()
      this.continuePlayTipTimer = setTimeout(() => {
        this.continuePlayTipVisible = false
      }, CONTINUE_PLAY_TIP_DURATION_MS)
    },

    clearContinuePlayTipTimer() {
      if (this.continuePlayTipTimer) {
        clearTimeout(this.continuePlayTipTimer)
        this.continuePlayTipTimer = null
      }
    },

    hideContinuePlayTip() {
      this.clearContinuePlayTipTimer()
      this.continuePlayTipVisible = false
    },

    restartPlaybackFromBeginning() {
      if (!this.videoProgressPlayer) return
      this.seekToSavedProgress(0)
      this.hideContinuePlayTip()
    },

    seekToSavedProgress(time) {
      if (!this.videoProgressPlayer) return
      const duration = this.getCurrentDuration() || this.pendingResumeDuration
      const rawTime = Number(time) || 0
      const safeTime = duration > 0
        ? Math.min(Math.max(rawTime, 0), Math.max(duration - 1, 0))
        : Math.max(rawTime, 0)

      this.videoProgressPlayer.currentTime = safeTime
      this.lastPlaybackSampleTime = safeTime
      this.lastSavedTime = safeTime
    },

    addBeforeUnloadListener() {
      window.addEventListener('beforeunload', this.handleBeforeUnload)
      window.addEventListener('pagehide', this.handlePageHide)
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
    },

    removeBeforeUnloadListener() {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
      window.removeEventListener('pagehide', this.handlePageHide)
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    },

    addProgressEventListeners() {
      if (!this.videoProgressPlayer || this.progressEventListenersBound) return

      this.videoProgressPlayer.addEventListener('play', this.handleVideoPlay)
      this.videoProgressPlayer.addEventListener('pause', this.handleVideoPause)
      this.videoProgressPlayer.addEventListener('timeupdate', this.handleVideoTimeUpdate)
      this.videoProgressPlayer.addEventListener('seeking', this.handleVideoSeeking)
      this.videoProgressPlayer.addEventListener('seeked', this.handleVideoSeeked)
      this.videoProgressPlayer.addEventListener('ended', this.handleVideoEnded)
      this.videoProgressPlayer.addEventListener('loadedmetadata', this.tryApplyPendingResume)
      this.videoProgressPlayer.addEventListener('canplay', this.tryApplyPendingResume)
      this.progressEventListenersBound = true
    },

    removeProgressEventListeners() {
      if (!this.videoProgressPlayer || !this.progressEventListenersBound) return

      this.videoProgressPlayer.removeEventListener('play', this.handleVideoPlay)
      this.videoProgressPlayer.removeEventListener('pause', this.handleVideoPause)
      this.videoProgressPlayer.removeEventListener('timeupdate', this.handleVideoTimeUpdate)
      this.videoProgressPlayer.removeEventListener('seeking', this.handleVideoSeeking)
      this.videoProgressPlayer.removeEventListener('seeked', this.handleVideoSeeked)
      this.videoProgressPlayer.removeEventListener('ended', this.handleVideoEnded)
      this.videoProgressPlayer.removeEventListener('loadedmetadata', this.tryApplyPendingResume)
      this.videoProgressPlayer.removeEventListener('canplay', this.tryApplyPendingResume)
      this.progressEventListenersBound = false
    },

    handleVideoPlay() {
      this.clearProgressPauseTimer()
      this.clearProgressSeekTimer()
      this.isProgressSeeking = false
      this.lastPlaybackSampleTime = this.getCurrentPlaybackTime()
      this.flushContinuePlayTip()
    },

    handleVideoPause() {
      this.lastPlaybackSampleTime = this.getCurrentPlaybackTime()
      this.schedulePauseProgressSave()
    },

    handleVideoTimeUpdate() {
      const video = this.videoProgressPlayer
      if (!video || video.paused || video.ended || this.isProgressSeeking) {
        this.lastPlaybackSampleTime = this.getCurrentPlaybackTime()
        return
      }

      const currentTime = this.getCurrentPlaybackTime()
      if (this.lastPlaybackSampleTime === null) {
        this.lastPlaybackSampleTime = currentTime
        return
      }

      const delta = currentTime - this.lastPlaybackSampleTime
      this.lastPlaybackSampleTime = currentTime

      if (delta <= 0) {
        return
      }

      const playbackRate = Number(video.playbackRate) > 0 ? Number(video.playbackRate) : 1
      const maxDelta = Math.max(MAX_VALID_PROGRESS_DELTA, playbackRate * MAX_VALID_PROGRESS_DELTA)
      if (delta > maxDelta) {
        return
      }

      this.actualPlayTime = this.roundSeconds(this.actualPlayTime + delta)
      if (this.actualPlayTime >= HEARTBEAT_PLAYED_SECONDS) {
        this.saveCurrentProgress()
      }
    },

    handleVideoSeeking() {
      this.isProgressSeeking = true
      this.clearProgressPauseTimer()
      this.clearProgressSeekTimer()
      this.lastPlaybackSampleTime = null
    },

    handleVideoSeeked() {
      this.isProgressSeeking = false
      this.lastPlaybackSampleTime = this.getCurrentPlaybackTime()
      this.scheduleSeekProgressSave()
    },

    handleVideoEnded() {
      this.lastPlaybackSampleTime = this.getCurrentPlaybackTime()
      this.saveCurrentProgress(true)
    },

    handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.saveCurrentProgress(true)
      }
    },

    handlePageHide() {
      this.saveCurrentProgress(true)
    },

    handleBeforeUnload() {
      this.saveProgressOnUnload()
    },

    schedulePauseProgressSave() {
      this.clearProgressPauseTimer()
      this.progressPauseTimer = setTimeout(() => {
        this.saveCurrentProgress(true)
      }, PAUSE_SAVE_DELAY_MS)
    },

    scheduleSeekProgressSave() {
      this.clearProgressSeekTimer()
      this.progressSeekTimer = setTimeout(() => {
        this.saveCurrentProgress(true)
      }, SEEK_SAVE_DELAY_MS)
    },

    clearProgressPauseTimer() {
      if (this.progressPauseTimer) {
        clearTimeout(this.progressPauseTimer)
        this.progressPauseTimer = null
      }
    },

    clearProgressSeekTimer() {
      if (this.progressSeekTimer) {
        clearTimeout(this.progressSeekTimer)
        this.progressSeekTimer = null
      }
    },

    getCurrentPlaybackTime() {
      if (!this.videoProgressPlayer) return 0
      const currentTime = Number(this.videoProgressPlayer.currentTime)
      if (!Number.isFinite(currentTime) || currentTime < 0) {
        return 0
      }
      return this.roundSeconds(currentTime)
    },

    getCurrentDuration() {
      if (!this.videoProgressPlayer) return 0
      const duration = Number(this.videoProgressPlayer.duration)
      if (!Number.isFinite(duration) || duration <= 0) {
        return 0
      }
      return this.roundSeconds(duration)
    },

    buildProgressPayload(force = false) {
      if (!this.videoProgressPlayer || !this.videoProgressId) {
        return null
      }

      const currentTime = this.getCurrentPlaybackTime()
      const duration = this.getCurrentDuration()
      if (!duration) {
        return null
      }

      const progress = Number(((currentTime / duration) * 100).toFixed(2))
      const playedSeconds = this.roundSeconds(this.actualPlayTime)
      const hasEnoughPlayedSeconds = playedSeconds >= HEARTBEAT_PLAYED_SECONDS
      const hasPositionChange = Math.abs(currentTime - this.lastSavedTime) >= MIN_SAVE_POSITION_DELTA

      if (!force && !hasEnoughPlayedSeconds) {
        return null
      }

      if (force && !hasPositionChange && playedSeconds <= 0) {
        return null
      }

      return {
        videoId: this.videoProgressId,
        currentTime,
        duration,
        progress,
        actualPlayTime: force ? playedSeconds : Math.max(HEARTBEAT_PLAYED_SECONDS, playedSeconds)
      }
    },

    async saveCurrentProgress(force = false) {
      const payload = this.buildProgressPayload(force)
      if (!payload) {
        return
      }

      if (this.progressSaveInFlight) {
        this.pendingProgressSave = true
        return
      }

      this.progressSaveInFlight = true
      try {
        await saveVideoProgress(payload)
        this.lastSavedTime = payload.currentTime
        if (typeof this.onPlaybackNetworkRecovered === 'function') {
          this.onPlaybackNetworkRecovered({ source: 'saveVideoProgress' })
        }
      } catch (error) {
        if (typeof this.onPlaybackNetworkError === 'function') {
          this.onPlaybackNetworkError(error, { source: 'saveVideoProgress' })
        }
      } finally {
        // 无论上报成功或失败，都重置本轮累计播放时长，避免异常时连续触发上报。
        this.actualPlayTime = 0
        this.progressSaveInFlight = false
        const shouldReplay = this.pendingProgressSave
        this.pendingProgressSave = false
        if (shouldReplay) {
          this.saveCurrentProgress(true)
        }
      }
    },

    saveProgressOnUnload() {
      const payload = this.buildProgressPayload(true)
      if (!payload) {
        return
      }

      const data = JSON.stringify(payload)
      const url = this.$store.state.HOST + '/study/saveVideoProgress'

      if (navigator.sendBeacon) {
        const blob = new Blob([data], { type: 'application/json' })
        navigator.sendBeacon(url, blob)
      } else {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url, false)
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.send(data)
      }

      this.lastSavedTime = payload.currentTime
      this.actualPlayTime = 0
    },

    roundSeconds(value) {
      return Math.round((Number(value) || 0) * 100) / 100
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },

    resetVideoProgressState() {
      this.clearContinuePlayTipTimer()
      this.progressSaveTimer = null
      this.hasCheckedProgress = false
      this.lastSavedTime = 0
      this.isProgressLoaded = false
      this.actualPlayTime = 0
      this.playTimerInterval = null
      this.videoProgressId = null
      this.videoProgressPlayer = null
      this.progressPauseTimer = null
      this.progressSeekTimer = null
      this.progressSaveInFlight = false
      this.pendingProgressSave = false
      this.lastPlaybackSampleTime = null
      this.isProgressSeeking = false
      this.progressEventListenersBound = false
      this.pendingResumeTime = null
      this.pendingResumeDuration = 0
      this.hasAppliedPendingResume = false
      this.continuePlayTipVisible = false
      this.continuePlayTipText = ''
      this.continuePlayTipTime = ''
      this.continuePlayTipTimer = null
      this.pendingContinuePlayTip = null
    },

    cleanupVideoProgress() {
      this.clearProgressPauseTimer()
      this.clearProgressSeekTimer()
      this.clearContinuePlayTipTimer()
      this.saveCurrentProgress(true)
      this.removeProgressEventListeners()
      this.removeBeforeUnloadListener()
      this.resetVideoProgressState()
    }
  },

  beforeRouteLeave(to, from, next) {
    this.cleanupVideoProgress()
    next()
  },

  beforeDestroy() {
    this.cleanupVideoProgress()
  }
}
